import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { Sparkles, Send, X, Loader2, Grape, Wand2, Check } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "¿Qué aromas debería buscar?",
  "Sugiéreme un maridaje",
  "¿Qué descriptores usar en boca?",
];

const SECTION_LABELS = { visual: "Visual", olfativo: "Olfativo", gustativo: "Gustativo", obs: "Conclusión" };
const FIELD_LABELS = {
  limpidez: "Limpidez", brillantez: "Brillantez", color: "Color", intensidad: "Intensidad", densidad: "Densidad",
  estado: "Estado", desarrollo: "Desarrollo", caracter: "Carácter aromático", tipo_aromas: "Tipo de aromas",
  aromas_Frutales: "Aromas frutales", aromas_Florales: "Aromas florales", aromas_Herbáceos: "Aromas herbáceos",
  aromas_Especiados: "Aromas especiados", aromas_Minerales: "Aromas minerales", aromas_Balsámicos: "Aromas balsámicos",
  aromas_Fermentativos: "Aromas fermentativos", aromas_Crianza: "Aromas de crianza",
  dulzor: "Dulzor", acidez: "Acidez", alcohol: "Alcohol", taninos_cantidad: "Taninos · cantidad", taninos_calidad: "Taninos · calidad",
  astringencia: "Astringencia", cuerpo: "Cuerpo", intensidad_sabor: "Intensidad de sabor", final: "Final", estilo: "Estilo",
  maridaje: "Maridaje", calidad: "Nivel de calidad", clima: "Clima", consumo: "Consumo", notas: "Notas", recomendaciones: "Recomendaciones",
};

function suggestionLines(s) {
  const lines = [];
  for (const [sec, block] of Object.entries(s || {})) {
    for (const [field, val] of Object.entries(block || {})) {
      lines.push({
        label: `${SECTION_LABELS[sec] || sec} · ${FIELD_LABELS[field] || field}`,
        value: Array.isArray(val) ? val.join(", ") : String(val),
      });
    }
  }
  return lines;
}

function hasSuggestions(s) {
  return s && Object.values(s).some((b) => b && Object.keys(b).length > 0);
}

export default function SommChat({ sessionId, getContext, onApply }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [confirmIdx, setConfirmIdx] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    api.get(`/somm/history`, { params: { session_id: sessionId } }).then((r) => setMessages(r.data)).catch(() => {});
    api.get(`/somm/usage`).then((r) => setRemaining(r.data.remaining)).catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("vinonotas-open-somm", openHandler);
    return () => window.removeEventListener("vinonotas-open-somm", openHandler);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/somm/chat", {
        session_id: sessionId,
        message: msg,
        context: getContext ? getContext() : "",
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, suggestions: data.suggestions }]);
      setRemaining(data.usage?.remaining);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(err.response?.data?.detail || "Límite diario de Somm alcanzado.");
      else toast.error(formatApiError(err.response?.data?.detail) || "Somm no está disponible ahora.");
      setMessages((m) => m.slice(0, -1));
      setInput(msg);
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = (idx) => {
    const s = messages[idx]?.suggestions;
    if (!s || !onApply) return;
    const n = onApply(s);
    setConfirmIdx(null);
    toast.success(n ? `${n} campo(s) rellenado(s) por Somm` : "Sugerencia aplicada");
  };

  return (
    <>
      {!open && (
        <button
          data-testid="somm-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full pl-4 pr-5 py-3 text-white shadow-2xl transition-transform duration-300 hover:-translate-y-1"
          style={{ background: "#5A1E2C" }}
        >
          <Sparkles className="w-5 h-5" style={{ color: "#C8A96A" }} />
          <span className="font-medium text-sm">Somm</span>
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            data-testid="somm-panel"
            className="fixed z-50 bg-white shadow-2xl border border-black/10 flex flex-col overflow-hidden
                       inset-x-0 bottom-0 h-[80vh] rounded-t-3xl
                       sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[380px] sm:h-[560px] sm:rounded-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ background: "#5A1E2C" }}>
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(200,169,106,0.2)" }}>
                  <Grape className="w-5 h-5" style={{ color: "#C8A96A" }} />
                </span>
                <div>
                  <p className="font-serif text-white text-lg leading-none">Somm Vinonotas</p>
                  <p className="text-[11px] text-white/60 mt-0.5">Sumiller IA {remaining != null && `· ${remaining} consultas hoy`}</p>
                </div>
              </div>
              <button data-testid="somm-close-btn" onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: "#FAF8F4" }}>
              {messages.length === 0 && !loading && (
                <div className="text-center py-6">
                  <Grape className="w-10 h-10 mx-auto mb-3" style={{ color: "#C8A96A" }} />
                  <p className="text-sm text-neutral-600 mb-1 font-medium">Hola, soy tu sumiller.</p>
                  <p className="text-xs text-neutral-400 mb-4 px-4">Pregúntame por aromas, descriptores de cata o maridajes para el vino que estás catando.</p>
                  <div className="flex flex-col gap-2 px-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid={`somm-suggestion`} onClick={() => send(s)}
                        className="text-sm px-3 py-2 rounded-xl border border-black/10 bg-white hover:border-[#5A1E2C]/40 transition-colors text-neutral-700">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
                    <div className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === "user" ? "text-white rounded-br-md ml-auto max-w-fit" : "bg-white border border-black/5 text-neutral-800 rounded-bl-md"
                    }`} style={m.role === "user" ? { background: "#5A1E2C" } : {}}>
                      {m.content}
                    </div>
                    {m.role === "assistant" && onApply && hasSuggestions(m.suggestions) && (
                      <div className="mt-1.5">
                        {confirmIdx === i ? (
                          <div data-testid="somm-confirm-box" className="rounded-xl border border-[#C8A96A]/50 bg-[#FBF7EF] p-3">
                            <p className="text-xs font-medium text-neutral-700 mb-2">Se rellenarán estos campos:</p>
                            <ul className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                              {suggestionLines(m.suggestions).map((l, k) => (
                                <li key={k} className="text-xs text-neutral-600 leading-snug">
                                  <span className="font-medium text-neutral-800">{l.label}:</span> {l.value}
                                </li>
                              ))}
                            </ul>
                            <div className="flex gap-2">
                              <button data-testid="somm-apply-confirm-btn" onClick={() => applySuggestion(i)}
                                className="flex-1 text-xs font-medium rounded-lg py-2 text-white flex items-center justify-center gap-1.5 transition-opacity hover:opacity-90" style={{ background: "#5A1E2C" }}>
                                <Check className="w-3.5 h-3.5" /> Rellenar ficha
                              </button>
                              <button data-testid="somm-apply-cancel-btn" onClick={() => setConfirmIdx(null)}
                                className="text-xs rounded-lg py-2 px-3 border border-black/10 text-neutral-600 hover:bg-black/5 transition-colors">
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button data-testid="somm-apply-btn" onClick={() => setConfirmIdx(i)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-[#C8A96A] bg-[#FBF7EF] text-[#5A1E2C] px-3 py-1.5 hover:bg-[#F4EAD6] transition-colors">
                            <Wand2 className="w-3.5 h-3.5" /> Aplicar sugerencia
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-black/5 rounded-2xl rounded-bl-md px-4 py-3">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#5A1E2C" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-black/5 shrink-0 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="somm-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Pregunta a Somm..."
                  rows={1}
                  className="flex-1 resize-none max-h-24 rounded-xl border border-black/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#5A1E2C]/50"
                />
                <button data-testid="somm-send-btn" onClick={() => send()} disabled={loading || !input.trim()}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 disabled:opacity-40 transition-opacity" style={{ background: "#5A1E2C" }}>
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
